import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';

const LENS_STATUS_ICON = {
  correct: <CheckCircleIcon color="success" fontSize="small" />,
  partial: <ErrorOutlineIcon color="warning" fontSize="small" />,
  missed: <ErrorOutlineIcon color="error" fontSize="small" />,
};

const SECTION_DEFS = {
  hackathon_score: {
    title: 'Score',
    resolve: (result) => (Number.isFinite(result.score) ? result : null),
    render: (result) => (
      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} alignItems="center">
        <Chip
          label={`${result.score}/${result.max} (${result.percent}%)`}
          color={result.percent >= 70 ? 'success' : result.percent >= 40 ? 'warning' : 'error'}
        />
        {result.mode && (
          <Chip size="small" variant="outlined" label={result.mode === 'llm' ? 'AI-graded' : 'Rule-graded'} />
        )}
      </Stack>
    ),
  },
  hackathon_evidence: {
    title: 'Evidence check',
    resolve: (result) => [result.evidence_check, result.guard_check].filter(Boolean),
    render: (checks) => (
      <Stack spacing={1}>
        {checks.map((check) => {
          const ratio = typeof check.passed === 'boolean'
            ? (check.passed ? 1 : 0)
            : (check.max ? check.score / check.max : 1);
          const status = ratio >= 0.99 ? 'correct' : ratio > 0 ? 'partial' : 'missed';
          return (
          <Box
            key={check.id}
            sx={{
              display: 'flex', gap: 1, alignItems: 'flex-start',
              p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
            }}
          >
            {LENS_STATUS_ICON[status]}
            <Box>
              <Typography variant="body2" fontWeight={700}>{check.label}</Typography>
              <Typography variant="caption" color="text.secondary">{check.detail}</Typography>
            </Box>
          </Box>
          );
        })}
      </Stack>
    ),
  },
  lens_results: {
    title: 'Understanding check',
    resolve: (result) => Object.entries(result.understanding?.lensResults || result.lensResults || {}),
    render: (entries) => (
      <Stack spacing={1}>
        {entries.map(([key, lens]) => (
          <Box
            key={key}
            sx={{
              display: 'flex', gap: 1, alignItems: 'flex-start',
              p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
            }}
          >
            {LENS_STATUS_ICON[lens.status] || LENS_STATUS_ICON.missed}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>{lens.label}</Typography>
              {lens.reason && (
                <Typography variant="caption" color="text.secondary" component="div">{lens.reason}</Typography>
              )}
              {lens.status !== 'correct' && lens.expected && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                  Expected direction: {lens.expected}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    ),
  },
  scores: {
    title: 'Scores',
    resolve: (result) => Object.entries(result.scores || {}),
    render: (entries) => (
      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
        {entries.map(([label, value]) => (
          <Chip
            key={label}
            label={`${label.replaceAll('_', ' ')}: ${value}`}
            color={label === 'recruiter_readiness' ? 'primary' : 'default'}
            variant={label === 'recruiter_readiness' ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
    ),
  },
  tools_detected: {
    title: 'Evidence detected',
    resolve: (result) => result.tools_detected || [],
    render: (tools) => (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
        {tools.map((tool) => (
          <Chip key={tool} size="small" label={tool} />
        ))}
      </Stack>
    ),
  },
  recommendations: {
    title: 'Recommendations',
    resolve: (result) => result.recommendations || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
  gaps: {
    title: 'Evidence gaps',
    resolve: (result) => result.evidence_gaps || result.gaps || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
  strongest_claims: {
    title: 'Strongest claims',
    resolve: (result) => result.strongest_claims || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
  weekly_summary: {
    title: 'Summary',
    resolve: (result) => result.weekly_summary || result.summary || result.understanding?.feedback || '',
    render: (text) => (
      <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
        <Typography variant="body2">{text}</Typography>
      </Box>
    ),
  },
  linkedin_post: {
    title: 'LinkedIn draft',
    resolve: (result) => result.linkedin_post || '',
    render: (text) => (
      <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default', whiteSpace: 'pre-wrap' }}>
        <Typography variant="body2">{text}</Typography>
      </Box>
    ),
  },
  readme_bullets: {
    title: 'README bullets',
    resolve: (result) => result.readme_bullets || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
  interview_prompts: {
    title: 'Interview prompts',
    resolve: (result) => result.interview_prompts || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
  next_actions: {
    title: 'Next actions',
    resolve: (result) => result.next_actions || [],
    render: (items) => (
      <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">{item}</Typography>
        ))}
      </Stack>
    ),
  },
};

const isEmptyValue = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return value.trim().length === 0;
  return !value;
};

export const EvidenceIntelligenceSections = ({
  state = 'idle',
  result,
  error,
  sections = [],
  loadingLabel = 'Analyzing evidence...',
  idleHint = 'Run the analysis to see recommendations.',
}) => {
  if (state === 'loading') {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">{loadingLabel}</Typography>
      </Stack>
    );
  }

  if (state === 'error') {
    return <Alert severity="error">{error || 'Analysis failed.'}</Alert>;
  }

  if (state === 'done' && result) {
    return (
      <Stack spacing={2.5}>
        {process.env.NODE_ENV === 'development' && (
          <Alert severity={result.mode === 'llm' ? 'success' : 'info'}>
            {result.mode === 'llm'
              ? `AI-enhanced review generated with ${result.model || 'configured model'}.`
              : 'Rules-based review shown. Configure a local LLM to unlock richer rewrites.'}
            {result.llm_error ? ` LLM fallback reason: ${result.llm_error}` : ''}
          </Alert>
        )}
        {sections.map((key) => {
          const def = SECTION_DEFS[key];
          if (!def) return null;
          const value = def.resolve(result);
          if (isEmptyValue(value)) return null;
          return (
            <Box key={key}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{def.title}</Typography>
              {def.render(value)}
            </Box>
          );
        })}
      </Stack>
    );
  }

  return <Typography variant="body2" color="text.secondary">{idleHint}</Typography>;
};

const EvidenceIntelligenceDialog = ({
  open,
  onClose,
  title,
  state,
  result,
  error,
  onRerun,
  rerunLabel = 'Run again',
  sections,
  loadingLabel,
  idleHint,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
      {title}
      <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <EvidenceIntelligenceSections
        state={state}
        result={result}
        error={error}
        sections={sections}
        loadingLabel={loadingLabel}
        idleHint={idleHint}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onRerun} disabled={state === 'loading'}>{rerunLabel}</Button>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default EvidenceIntelligenceDialog;
